import { Feature } from '@tencent/workbench';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import { IPositionFeature } from './position-interface';
import { convertCmToPx, convertPxToCm } from '@src/slide/infrastructure/module/independent/unit';
import { NumberInputFrom } from '@tencent/wb-ext-form';
import { ELeftOrTop, ESizeFrom, SpinOptions } from './config';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import type { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import type { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IBaseFrameProperty } from '@src/slide/domain/ui-model/element-modeles/property/frame/base/base.interface';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { INPUT_PROPS_CALLBACK_KEY } from '../../../constants';

@Feature()
export class PositionFeature extends BaseDrawerFeature implements IPositionFeature {
    public horizontalFrom = ESizeFrom.LEFT_TOP;
    public verticalFrom = ESizeFrom.LEFT_TOP;

    public constructor() {
        super();
    }

    public get left(): number {
        const value = this.modifyValue(this.getFrame()?.getLeft(true), ELeftOrTop.LEFT, true);
        return this.getFinalSize(value);
    }

    public get top(): number {
        const value = this.modifyValue(this.getFrame()?.getTop(true), ELeftOrTop.TOP, true);
        return this.getFinalSize(value);
    }

    public handleHorizontalFromChange(value: ESizeFrom): void {
        if (value === this.horizontalFrom) return;
        this.horizontalFrom = value;
        const pxValue = this.getSizeAfterFromChange(ELeftOrTop.LEFT);
        this.getFrame()?.updateLeft(pxValue, true);
    }

    public handleVerticalFromChange(value: ESizeFrom): void {
        if (value === this.verticalFrom) return;
        this.verticalFrom = value;
        const pxValue = this.getSizeAfterFromChange(ELeftOrTop.TOP);
        this.getFrame()?.updateTop(pxValue, true);
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.POSITION)
    public handleLeftChange(value: number, from?: NumberInputFrom): void {
        if (this.isInputOnlyChangeUI()) return;
        const pxVal = this.getChangedVal(value, ELeftOrTop.LEFT, from === NumberInputFrom.INPUT, true);
        this.getFrame()?.updateLeft(pxVal, true);
        this.tdwDrawerReportNumberInput(
            this.tdwDrawerConstMap.elementPositon,
            { ver6: convertPxToCm(pxVal), ver7: 1 }, from,
        );
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.POSITION)
    public handleTopChange(value: number, from?: NumberInputFrom): void {
        if (this.isInputOnlyChangeUI()) return;
        const pxVal = this.getChangedVal(value, ELeftOrTop.TOP, from === NumberInputFrom.INPUT, true);
        this.getFrame()?.updateTop(pxVal, true);
        this.tdwDrawerReportNumberInput(
            this.tdwDrawerConstMap.elementPositon,
            { ver6: convertPxToCm(pxVal), ver7: 2 }, from,
        );
    }

    /**
     * from变更后，相应的元素的left/top也需要变更。获取变更值
     * @param type
     * @returns
     */
    private getSizeAfterFromChange = (type: ELeftOrTop) => {
        const value = type === ELeftOrTop.LEFT
            ? this.getFrame()?.getLeft(true)
            : this.getFrame()?.getTop(true);
        let pxValue = this.getChangedVal(this.getFinalSize(value), type);
        const { sizeFrom, diff } = this.getSizeFromAndDiff(type);
        // modify pxValue
        sizeFrom === ESizeFrom.CENTER
            ? pxValue += diff
            : pxValue -= diff;
        return pxValue;
    };

    /**
     * 修正value，单位为px。如果是居中，则需要变化时＋或显示时-slide的宽度/高度的一半
     */
    private modifyValue = (pxValue: number, type: ELeftOrTop, isForShown?: boolean): number => {
        const { sizeFrom, diff } = this.getSizeFromAndDiff(type);
        if (sizeFrom === ESizeFrom.CENTER) {
            pxValue += (diff * (isForShown ? -1 : 1));
        }
        return pxValue;
    };

    /**
     * 获取变更值，主要处理多选且高度或者宽度不同的情况
     */
    private getChangedVal = (
        value: number, type: ELeftOrTop,
        isFromInput?: boolean, needModify?: boolean,
    ): number => {
        const selectedModel = this.getSelectModel() as unknown as IElementBaseModel | IMultiplyModel;
        const pxValue = convertCmToPx(Number(value));
        const changedVal = needModify ? this.modifyValue(pxValue, type) : pxValue;
        if (selectedModel.type !== UIModelType.MULTIPLY || !isNaN(this[type])) {
            return changedVal;
        }
        // 当多选元素大小不同时，根据selectedModels中第一个不为table或本地文件的值的大小进行变化
        const [firstModel] = (selectedModel as IMultiplyModel).selectedModels;
        if (firstModel && !isFromInput) {
            return this.getFirstModelValueForMultiply(type, firstModel, value);
        }
        return changedVal;
    };

    private getFirstModelValueForMultiply = (
        type: ELeftOrTop, firstModel: IElementBaseModel,
        value: number,
    ) => {
        let cmValue = convertPxToCm(type === ELeftOrTop.LEFT
            ? firstModel.getFrame()?.getLeft(true)
            : firstModel.getFrame()?.getTop(true));
        if (isNaN(value)) {
            return convertCmToPx(cmValue);
        }
        // 当多选大小不同时，-按钮时传入的value为0，+按钮时value为step
        cmValue = this.validNumber(
            value === 0 ? cmValue - SpinOptions.step : cmValue + SpinOptions.step,
            SpinOptions.max,
            SpinOptions.min,
        );
        return convertCmToPx(cmValue);
    };

    private getSizeFromAndDiff = (type: ELeftOrTop): { sizeFrom: ESizeFrom, diff: number } => {
        const { slideWidth, slideHeight } = this.getUiDataService().presentation.props;
        let sizeFrom: ESizeFrom;
        let diff: number;
        if (type === ELeftOrTop.LEFT) {
            sizeFrom = this.horizontalFrom;
            diff = slideWidth / 2;
        } else {
            sizeFrom = this.verticalFrom;
            diff = slideHeight / 2;
        }
        return { sizeFrom, diff };
    };

    private getFinalSize = (value: number): number => (isNaN(value) ? Number.NaN : convertPxToCm(value));

    private getFrame = (): IBaseFrameProperty => (this.getSelectModel() as IElementBaseModel | IMultiplyModel)
        .getFrame?.();
}
